import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import type { components } from '@/../types/api'

export type SchedulerConfigOut = components['schemas']['SchedulerConfigOut']
export type SchedulerConfigIn = components['schemas']['SchedulerConfigIn']
export type CycleConfigOut = components['schemas']['CycleConfigOut']
// Per-channel LLM routing mode. Local augmentation until the OpenAPI typegen
// is re-run — the backend ChannelConfig{Out,PatchIn} already carry it.
export type LlmProviderMode = 'auto' | 'local' | 'cloud'
// Local augmentations until the OpenAPI typegen is re-run — the backend
// ChannelConfig{Out,PatchIn} + ChannelScheduleStateOut already carry these.
export type ChannelConfigOut = components['schemas']['ChannelConfigOut'] & {
  llm_provider?: LlmProviderMode
  upload_time?: string
}
export type ChannelConfigPatchIn = components['schemas']['ChannelConfigPatchIn'] & {
  llm_provider?: LlmProviderMode
  upload_time?: string
}
export type ChannelScheduleStateOut = components['schemas']['ChannelScheduleStateOut'] & {
  upload_time?: string
  upload_last_run_date?: string
  upload_last_run_status?: string
  upload_started_at?: string
  upload_ended_at?: string
  upload_next_run_at?: string | null
}
export type SchedulerStateOut = components['schemas']['SchedulerStateOut']
export type SimulateOut = components['schemas']['SimulateOut']
export type KickoffOut = components['schemas']['KickoffOut']
export type GlobalEnabledOut = components['schemas']['GlobalEnabledOut']
export type GlobalEnabledIn = components['schemas']['GlobalEnabledIn']
export type RetentionPreviewOut = components['schemas']['RetentionPreviewOut']
export type RetentionPurgeIn = components['schemas']['RetentionPurgeIn']
export type RetentionPurgeOut = components['schemas']['RetentionPurgeOut']
export type SchedulableFactoryOut = components['schemas']['SchedulableFactoryOut']
export type CronExecutionOut = components['schemas']['CronExecutionOut']
export type CortesSourceIn = components['schemas']['CortesSourceIn']
export type CortesSourceOut = components['schemas']['CortesSourceOut']
export type StudioThumbnailsOut = components['schemas']['StudioThumbnailsOut']
export type StudioThumbnailsRunOut = components['schemas']['StudioThumbnailsRunOut']
export type StudioThumbnailQueueItemOut = components['schemas']['StudioThumbnailQueueItemOut']
export type StudioThumbnailUnqueueOut = components['schemas']['StudioThumbnailUnqueueOut']

/**
 * Fila das capas dos Shorts do usuário e se dá para drenar agora (card #247).
 * Enquanto uma drenagem roda, consulta de 15 em 15 s para o cartão voltar
 * sozinho quando ela acabar.
 */
export function useStudioThumbnails() {
  return useQuery({
    queryKey: queryKeys.scheduler.studioThumbnails(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/scheduler/studio-thumbnails')
      if (!data) throw new Error('studio thumbnails empty')
      return data.data
    },
    refetchInterval: (query) => (query.state.data?.running ? 15_000 : 60_000),
  })
}

/**
 * "Aplicar capas pendentes": dispara a drenagem (só admin; 409 se já roda, 429
 * no 2º clique em 60 s). Com ``channel``, só as capas daquele canal (o botão do
 * card no Scheduler).
 */
export function useRunStudioThumbnails() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (channel?: string) => {
      const { data } = await api.POST('/api/v1/scheduler/studio-thumbnails/run', {
        params: { query: channel ? { channel } : {} },
      })
      if (!data) throw new Error('studio thumbnails run empty')
      return data.data
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.studioThumbnails() })
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
    },
  })
}

/**
 * Tira um vídeo da fila de capas, de vez (404 se ele não está na fila do
 * usuário; 409 enquanto uma drenagem roda). Para a capa que o YouTube nunca
 * aceita e voltaria em toda drenagem.
 */
export function useUnqueueStudioThumbnail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (uploadId: number) => {
      const { data } = await api.DELETE('/api/v1/scheduler/studio-thumbnails/queue/{upload_id}', {
        params: { path: { upload_id: uploadId } },
      })
      if (!data) throw new Error('studio thumbnails unqueue empty')
      return data.data
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.studioThumbnails() })
    },
  })
}

/** Full scheduler config (generation + upload cycles + per-channel). */
export function useSchedulerConfig() {
  return useQuery({
    queryKey: queryKeys.scheduler.config(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/scheduler/config')
      if (!data) throw new Error('scheduler config empty')
      return data.data
    },
    staleTime: 10_000,
  })
}

/** Global scheduler state — used to show 'last run' for generation/upload. */
export function useSchedulerState() {
  return useQuery({
    queryKey: queryKeys.scheduler.state(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/scheduler/state')
      if (!data) throw new Error('scheduler state empty')
      return data.data
    },
    // 10s polling — state ticks when a cycle finishes; the manual triggers
    // already invalidate on success so this mostly catches cron-driven runs.
    refetchInterval: 10_000,
    staleTime: 5_000,
  })
}

/** Per-channel schedule state (config + last run + computed next_run_at). */
export function useSchedulerChannels() {
  return useQuery({
    queryKey: queryKeys.scheduler.channels(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/scheduler/channels')
      if (!data) throw new Error('scheduler channels empty')
      return data.data
    },
    refetchInterval: 10_000,
    staleTime: 5_000,
  })
}

export function useChannelScheduleHistory(slug: string, days = 7) {
  return useQuery({
    queryKey: queryKeys.scheduler.channelHistory(slug, days),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/scheduler/channels/{slug}/history', {
        params: { path: { slug }, query: { days } },
      })
      if (!data) throw new Error('channel history empty')
      return data.data
    },
    enabled: Boolean(slug),
    staleTime: 30_000,
  })
}

/**
 * Effective global flag — AND of ``generation.enabled`` and ``upload.enabled``.
 * Pause cycles instead of touching per-channel rows: flipping this off
 * pauses everything; flipping it on restores each channel's prior state.
 */
export function useGlobalEnabled() {
  return useQuery({
    queryKey: queryKeys.scheduler.globalEnabled(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/scheduler/global-enabled')
      if (!data) throw new Error('global-enabled empty')
      return data.data
    },
    staleTime: 10_000,
  })
}

/**
 * Factories the scheduler can actually dispatch (``generation_runner._ADAPTERS``).
 * Feeds the "Factory" dropdown of the channel dialog. It used to be a hardcoded
 * copy that drifted both ways — missing ``cortes``, listing factories the cycle
 * refuses — so the backend is the only source now.
 */
export function useSchedulableFactories() {
  return useQuery({
    queryKey: queryKeys.scheduler.factories(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/scheduler/factories')
      if (!data) throw new Error('scheduler factories empty')
      return data.data.items
    },
    staleTime: 5 * 60_000,
  })
}

/**
 * Sources the Cortes factory polls for a channel (card #119). ``enabled`` gates
 * the request so a channel on any other factory never hits the endpoint.
 */
export function useCortesSources(slug: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.scheduler.cortesSources(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/scheduler/channels/{slug}/cortes-sources', {
        params: { path: { slug } },
      })
      if (!data) throw new Error('cortes sources empty')
      return data.data.items
    },
    enabled: Boolean(slug) && enabled,
    staleTime: 10_000,
  })
}

// --- Mutations ---------------------------------------------------------------

/** Replace the whole Cortes source list of a channel (URLs left out are removed). */
export function useSaveCortesSources(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (items: CortesSourceIn[]) => {
      const { data } = await api.PUT('/api/v1/scheduler/channels/{slug}/cortes-sources', {
        params: { path: { slug } },
        body: { items },
      })
      if (!data) throw new Error('cortes sources save empty')
      return data.data.items
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.cortesSources(slug) })
    },
  })
}

/** Atomically enable/disable both cycles. Per-channel rows are left untouched. */
export function useSetGlobalEnabled() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data } = await api.POST('/api/v1/scheduler/global-enabled', {
        body: { enabled },
      })
      if (!data) throw new Error('global-enabled update empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
    },
  })
}

/** Replace the entire scheduler config. Consumers must pass a full payload. */
export function useSaveSchedulerConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: SchedulerConfigIn) => {
      const { data } = await api.PUT('/api/v1/scheduler/config', { body: input })
      if (!data) throw new Error('scheduler save empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
    },
  })
}

/** Partial update for a channel's schedule row. */
export function usePatchChannelSchedule(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ChannelConfigPatchIn) => {
      const { data } = await api.PATCH('/api/v1/scheduler/channels/{slug}/config', {
        params: { path: { slug } },
        body: input,
      })
      if (!data) throw new Error('channel patch empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
      // Um canal de pool pausado, ou com outro uploads/dia, muda o consumo do pool.
      void qc.invalidateQueries({ queryKey: queryKeys.pools.all })
    },
  })
}

/** Fire the generation cycle for every enabled channel. */
export function useRunGenerationAll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/scheduler/generation/run')
      if (!data) throw new Error('generation run empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
    },
  })
}

export function useRunUploadAll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/scheduler/upload/run')
      if (!data) throw new Error('upload run empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
    },
  })
}

export function useRunChannelGeneration() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (slug: string) => {
      const { data } = await api.POST('/api/v1/scheduler/channels/{slug}/generation/run', {
        params: { path: { slug } },
      })
      if (!data) throw new Error('channel generation run empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
    },
  })
}

export function useRunChannelUpload() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (slug: string) => {
      const { data } = await api.POST('/api/v1/scheduler/channels/{slug}/upload/run', {
        params: { path: { slug } },
      })
      if (!data) throw new Error('channel upload run empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
      void qc.invalidateQueries({ queryKey: queryKeys.jobs.all })
    },
  })
}

/**
 * Fire the cleanup cycle (expira não-publicados fora da janela + eviction
 * do cache de clipes). Fire-and-forget no backend; 429 = cooldown 60s.
 */
export function useRunCleanupAll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/scheduler/cleanup/run')
      if (!data) throw new Error('cleanup run empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
    },
  })
}

/**
 * Dry-run da purga de publicados: quantos vídeos / bytes sairiam do disco
 * com o corte dado. ``enabled`` deve ficar true só com o dialog aberto.
 */
export function useRetentionPurgePreview(before: string, channel: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.scheduler.retentionPreview(before, channel),
    queryFn: async (): Promise<RetentionPreviewOut> => {
      const { data } = await api.GET('/api/v1/scheduler/retention/purge/preview', {
        params: { query: { before, ...(channel ? { channel } : {}) } },
      })
      if (!data) throw new Error('retention preview empty')
      return data.data
    },
    enabled,
    staleTime: 0,
  })
}

/** Executa a purga (síncrona no backend — a resposta traz o resumo). */
export function useRetentionPurge() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: RetentionPurgeIn): Promise<RetentionPurgeOut> => {
      const { data } = await api.POST('/api/v1/scheduler/retention/purge', { body: input })
      if (!data) throw new Error('retention purge empty')
      return data.data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.videos.all })
      void qc.invalidateQueries({ queryKey: queryKeys.scheduler.all })
    },
  })
}

/**
 * Dry-run the upload cycle for a single channel. Returns the ordered
 * backlog (what would publish) + quarantined entries. Pure read — doesn't
 * mutate state, so no onSuccess invalidation.
 */
export function useSimulateUpload() {
  return useMutation({
    mutationFn: async (slug: string): Promise<SimulateOut> => {
      const { data } = await api.POST(
        '/api/v1/scheduler/channels/{slug}/simulate-upload',
        {
          params: { path: { slug } },
        },
      )
      if (!data) throw new Error('simulate-upload empty')
      return data.data
    },
  })
}

/**
 * Heartbeat das rotinas do scheduler (``cron_executions``). A página não lista
 * mais as rotinas: só avisa quando alguma está com erro ou parada.
 */
export function useCronHealth() {
  return useQuery({
    queryKey: queryKeys.scheduler.cronHealth(),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/scheduler/cron-health')
      if (!data) throw new Error('cron health empty')
      return data.data.items
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}
